// utils/repartoNetoFactura.js
//
// Reparto del NETO REAL de una factura SII entre los conceptos de venta que comparten esa
// factura. Una factura de gestión puede cubrir varias torres del mismo edificio (dos
// mensualidades ya definidas en Cartelería, dos `negocios_carteleria` distintos vinculados a
// la MISMA factura vía el puente `facturas_sii_vinculaciones`). El puente es la fuente única
// de "qué conceptos comparten la factura" (regla 10.190); acá SOLO se deriva el display para
// mostrar — no se escribe nada.
//
// El peso de cada concepto es su importe PROYECTADO (`getImporteFacturable`): el reparto por
// proyectado reproduce exactamente el split intra-negocio que hacía `feeProporcion`
// (gestión+contenidos vs opex de un mismo negocio) y además agrega el split cross-negocio
// (torre I vs torre II), sin ningún caso especial.
//
// Garantía dura: la suma de las partes es EXACTA = round(neto de la factura) — método del
// resto mayor (largest remainder), determinista.

// Reparte `total` en enteros proporcionales a `pesos`, sumando EXACTO round(total).
// Sin pesos válidos (suma 0) → reparto parejo. Estable: desempata por índice.
export function repartirEntero(total, pesos) {
  const T = Math.round(Number(total) || 0);
  const n = pesos.length;
  if (n === 0) return [];
  const sum = pesos.reduce((a, p) => a + (Number(p) || 0), 0);
  const base = sum > 0 ? pesos.map((p) => (T * (Number(p) || 0)) / sum) : pesos.map(() => T / n);
  const floors = base.map((x) => Math.floor(x));
  const resto = T - floors.reduce((a, b) => a + b, 0);
  // Repartir el resto (0..n-1 pesos enteros) a las mayores fracciones.
  const orden = base.map((x, i) => ({ i, frac: x - Math.floor(x) })).sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; k < resto; k++) floors[orden[k % n].i] += 1;
  return floors;
}

// Construye Map<oportunidad.id, montoAsignado(int)> con la parte del neto real que le toca a
// cada concepto-fila que esté vinculado a una factura SII.
// - `oportunidades`: filas concepto-mes (usar el set COMPLETO, no el filtrado, para que el
//   reparto sea independiente de los filtros de UI).
// - `resolver(op)` → { facturaId, montoNeto } | null (típicamente `getVinculacionSII`).
// - `peso(op)` → number (importe proyectado del concepto).
// - `incluir(op)` → bool (p.ej. excluir moneda USD, que muestra proyectado).
//
// Las filas sin vínculo (o excluidas) NO entran al Map → el caller cae al proyectado.
// Una factura con un solo concepto → ese concepto recibe el neto entero (comportamiento
// idéntico al anterior).
export function construirRepartoNetoFactura(oportunidades, { resolver, peso, incluir } = {}) {
  const porFactura = new Map(); // facturaId -> { neto, ops: [] }
  for (const op of oportunidades || []) {
    if (incluir && !incluir(op)) continue;
    const v = resolver ? resolver(op) : null;
    if (!v || v.facturaId == null || v.montoNeto == null) continue;
    if (!porFactura.has(v.facturaId)) porFactura.set(v.facturaId, { neto: v.montoNeto, ops: [] });
    porFactura.get(v.facturaId).ops.push(op);
  }
  const out = new Map();
  for (const { neto, ops } of porFactura.values()) {
    const partes = repartirEntero(
      neto,
      ops.map((o) => (peso ? peso(o) : 0))
    );
    ops.forEach((o, idx) => out.set(o.id, partes[idx]));
  }
  return out;
}
